import { useRef, useEffect, RefObject } from "react";

interface UseInfiniteScrollResult {
  sentinelRef: RefObject<HTMLDivElement | null>;
}

export function useInfiniteScroll(loadMore: () => void, hasMore: boolean): UseInfiniteScrollResult {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(loadMore);
  const hasMoreRef = useRef(hasMore);

  loadMoreRef.current = loadMore;
  hasMoreRef.current = hasMore;

  // Re-creates the observer whenever loadMore or hasMore changes so that a
  // sentinel already in the viewport re-triggers immediately after each page
  // load completes.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  return { sentinelRef };
}
